import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { EmptyState } from "@/components/ui/feedback";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { buttonVariants } from "@/components/ui/button";
import { useHotWalletPolicyProposals } from "@/hooks/use-hot-wallet-policy-proposals";
import { useHotWalletsLocale } from "@/i18n/use-hot-wallets-locale";
import { cn } from "@/lib/utils";

/**
 * HW-S5: developer & policy console (SPEC-HW-8, WORKFLOW.md §3).
 * `/developer/hot-wallets`. SPEC-HW-8 §1 says API keys and webhooks are
 * reused, not forked: this page links out to the existing, real
 * `/developer/api-keys` and `/developer/webhooks` pages rather than
 * duplicating them, and adds only the hot-wallets-specific policy-proposal
 * panel (GET .../policy-proposals, SPEC-HW-8 §2), wired the same way as the
 * other three pages in this lane.
 */
/**
 * `summary` and `status` are optional on ApiHotWalletPolicyProposal (HW-L8
 * registers the route; the shape is not frozen), so a response can omit
 * either one. An empty <span> reads as "loaded and empty" when the truth is
 * "the endpoint supplied no value", so an absent or whitespace-only value
 * renders the same explicit unvalued dash the sibling dashboard.tsx already
 * uses. Copy-free on purpose: the dash is the lane's existing unvalued
 * convention, not a new user-facing string.
 */
const UNKNOWN_VALUE = "—";

export default function HotWalletConsolePage() {
  const { ui, dir } = useHotWalletsLocale();
  const { proposals, loading, error, refresh } = useHotWalletPolicyProposals();

  return (
    <div dir={dir}>
      <PageHeader title={ui["console.title"]} subtitle={ui["console.subtitle"]} />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface px-5 py-4">
          <div className="text-sm font-semibold text-ink">{ui["console.apiKeysSectionTitle"]}</div>
          <Link to="/developer/api-keys" className={cn(buttonVariants({ size: "sm", variant: "secondary" }), "mt-3 inline-flex")}>
            {ui["console.apiKeysLinkLabel"]}
          </Link>
        </div>
        <div className="rounded-lg border border-line bg-surface px-5 py-4">
          <div className="text-sm font-semibold text-ink">{ui["console.webhooksSectionTitle"]}</div>
          <Link to="/developer/webhooks" className={cn(buttonVariants({ size: "sm", variant: "secondary" }), "mt-3 inline-flex")}>
            {ui["console.webhooksLinkLabel"]}
          </Link>
        </div>
      </div>

      <h2 className="mb-3 text-base font-bold text-ink">{ui["console.policySectionTitle"]}</h2>

      {loading && <LoadingState rows={2} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2">
          <NotAvailableState
            title={ui["console.policyUnavailableTitle"]}
            description={ui["console.policyUnavailableDescription"]}
            reason={error.reasonCode}
          />
          <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          <button
            type="button"
            onClick={refresh}
            className="inline-flex min-h-[36px] items-center text-sm font-medium text-primary underline"
          >
            {ui["common.retry"]}
          </button>
        </div>
      )}

      {!loading && !error && proposals.length === 0 && (
        <EmptyState card title={ui["console.policyEmptyTitle"]} description={ui["console.policyEmptyDescription"]} />
      )}

      {!loading && !error && proposals.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {proposals.map((p) => (
            <li key={p.proposalId} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-3 text-sm">
              {/* A proposal summary is free text of unknown length; `min-w-0`
                  + `break-words` keeps it inside the row at 375px while the
                  short status keeps its own width. */}
              <span className="min-w-0 break-words font-semibold text-ink">{p.summary?.trim() || UNKNOWN_VALUE}</span>
              <span className="shrink-0 text-muted">{p.status?.trim() || UNKNOWN_VALUE}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
